import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWebsiteContactSubmissions, useWebsiteContactMemberSubmissions } from "@/queries/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { formatDateTime } from "@/lib/date-utils";
import { Mail } from "lucide-react";
import type { WebsiteAdminListContactSubmissionsOutput } from "@satyrsmc/shared/dto/admin/websiteAdmin";

type ContactSubmission = WebsiteAdminListContactSubmissionsOutput["items"][number];

const LIMIT = 20;
const CONTACT_TAB = "contact-us";
const MEMBER_TAB = "contact-member";

export function WebsiteContactSubmissionsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === MEMBER_TAB ? MEMBER_TAB : CONTACT_TAB;
  const contactPage = Math.max(1, parseInt(searchParams.get("contactPage") ?? "1", 10) || 1);
  const memberPage = Math.max(1, parseInt(searchParams.get("memberPage") ?? "1", 10) || 1);

  const {
    data: contactData,
    isLoading: loadingContact,
    isFetching: fetchingContact,
    isPlaceholderData: contactPlaceholder,
  } = useWebsiteContactSubmissions({ page: contactPage, limit: LIMIT });
  const {
    data: memberData,
    isLoading: loadingMember,
    isFetching: fetchingMember,
    isPlaceholderData: memberPlaceholder,
  } = useWebsiteContactMemberSubmissions({ page: memberPage, limit: LIMIT });

  const [viewing, setViewing] = useState<ContactSubmission | null>(null);

  const contactSubmissions = contactData?.items ?? [];
  const contactTotal = contactData?.total ?? 0;
  const memberSubmissions = memberData?.items ?? [];
  const memberTotal = memberData?.total ?? 0;

  function setTab(next: string) {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params);
  }

  function setPage(key: "contactPage" | "memberPage", value: number) {
    const params = new URLSearchParams(searchParams);
    params.set(key, String(value));
    setSearchParams(params);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            Contact submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value={CONTACT_TAB}>Contact us ({contactTotal})</TabsTrigger>
              <TabsTrigger value={MEMBER_TAB}>Contact member ({memberTotal})</TabsTrigger>
            </TabsList>

            <TabsContent value={CONTACT_TAB} className="space-y-4 pt-4">
              <p className="text-muted-foreground">
                Submissions from the public &quot;Contact us&quot; form (POST{" "}
                <code className="text-xs bg-muted px-1 rounded">/api/website/contact</code>).
              </p>
              {loadingContact ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : contactSubmissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No submissions yet.</p>
              ) : (
                <div className={`transition-opacity ${contactPlaceholder ? "opacity-50" : ""}`}>
                  <ul className="space-y-2">
                    {contactSubmissions.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => setViewing(s)}
                          className="w-full rounded-md border px-3 py-2 text-sm text-left hover:bg-accent"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span>
                              <span className="font-medium">{s.name}</span>
                              <span className="text-muted-foreground"> — {s.email}</span>
                              {s.subject && (
                                <span className="text-muted-foreground"> — {s.subject}</span>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDateTime(s.created_at)}
                            </span>
                          </div>
                          <p className="mt-1 text-muted-foreground truncate">{s.message}</p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {contactTotal > LIMIT && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {fetchingContact && !loadingContact ? (
                      <span className="italic">Loading…</span>
                    ) : (
                      <>
                        Showing {(contactPage - 1) * LIMIT + 1}–
                        {Math.min(contactPage * LIMIT, contactTotal)} of {contactTotal}
                      </>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={contactPage <= 1 || contactPlaceholder}
                      onClick={() => setPage("contactPage", contactPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={contactPage * LIMIT >= contactTotal || contactPlaceholder}
                      onClick={() => setPage("contactPage", contactPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value={MEMBER_TAB} className="space-y-4 pt-4">
              <p className="text-muted-foreground">
                Submissions from the &quot;Contact member&quot; form (POST{" "}
                <code className="text-xs bg-muted px-1 rounded">/api/website/contact-member</code>
                ).
              </p>
              {loadingMember ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : memberSubmissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No submissions yet.</p>
              ) : (
                <div className={`transition-opacity ${memberPlaceholder ? "opacity-50" : ""}`}>
                  <ul className="space-y-2">
                    {memberSubmissions.map((s) => (
                      <li key={s.id} className="rounded-md border px-3 py-2 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span>
                            <span className="font-medium">{s.sender_name}</span>
                            <span className="text-muted-foreground"> — {s.sender_email}</span>
                            <span className="text-muted-foreground">
                              {" "}
                              (member: {s.member_display_name ?? s.member_id})
                            </span>
                          </span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(s.created_at)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {memberTotal > LIMIT && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {fetchingMember && !loadingMember ? (
                      <span className="italic">Loading…</span>
                    ) : (
                      <>
                        Showing {(memberPage - 1) * LIMIT + 1}–
                        {Math.min(memberPage * LIMIT, memberTotal)} of {memberTotal}
                      </>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={memberPage <= 1 || memberPlaceholder}
                      onClick={() => setPage("memberPage", memberPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={memberPage * LIMIT >= memberTotal || memberPlaceholder}
                      onClick={() => setPage("memberPage", memberPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto max-w-2xl">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.name}</DialogTitle>
                <DialogDescription>
                  {viewing.email}
                  {viewing.subject ? ` — ${viewing.subject}` : null}
                  {" · "}
                  {formatDateTime(viewing.created_at)}
                </DialogDescription>
              </DialogHeader>
              <p className="text-sm whitespace-pre-wrap">{viewing.message}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
